import Colors from '../../constants/Colors';
import { enrichRowsWithCostUpdates } from '../ProjectProgress/progressHelpers';

/**
 * Calculate total material cost updates from project cost updates
 * @param {Array} projectCostUpdates - Array of project cost updates
 * @returns {number} Total material cost updates
 */
export const calculateTotalMaterialCostUpdates = (projectCostUpdates) => projectCostUpdates
  .filter((u) => u.isBudgetUpdate && u.type === 'material')
  .reduce((sum, u) => {
    if (u.estimatedCost) return sum + +u.estimatedCost;
    if (u.totalBilling) return sum + +u.totalBilling;
    if (u.billing) return sum + +u.billing;
    const qty = u.quantity ?? 1;
    const unit = u.cost ?? 0;
    return sum + unit * qty;
  }, 0);

/**
 * Calculate total equipment cost updates from project cost updates
 * @param {Array} projectCostUpdates - Array of project cost updates
 * @returns {number} Total equipment cost updates
 */
export const calculateTotalEquipmentCostUpdates = (projectCostUpdates) => projectCostUpdates
  .filter((u) => u.isBudgetUpdate && u.type === 'equipment')
  .reduce((sum, u) => {
    if (u.estimatedCost) return sum + Number(u.estimatedCost);
    if (u.totalBilling) return sum + Number(u.totalBilling);
    if (u.total) return sum + Number(u.total);

    let dailyCost = 0;
    let hourlyCost = 0;

    if (u.days && u.dailyCost) {
      dailyCost = u.dailyCost * u.days;
    }
    if (u.hours && u.hourlyCost) {
      hourlyCost = u.hourlyCost * u.hours;
    }

    // Sum both calculations if both are available
    return sum + dailyCost + hourlyCost;
  }, 0);

/**
 * Calculate equipment cost based on days and daily wage, or hours and hourly wage
 * @param {Object} record - Record containing equipment data
 * @param {number} equipmentCategoryId - Equipment category ID
 * @param {number} record.days - Number of days
 * @param {number} record.dailyWage - Daily wage rate
 * @param {number} record.hours - Number of hours
 * @param {number} record.hourlyWage - Hourly wage rate
 * @param {number} record.estimatedCost - Fallback estimated cost
 * @returns {number} Calculated cost
 */
export const calculateEquipmentCost = (record, equipmentCategoryId) => {
  let dailyCost = 0;
  let hourlyCost = 0;

  const {
    dailyWage,
    hourlyWage,
    categoryAssignments: {
      [equipmentCategoryId]: {
        days,
        hours,
      } = {},
    } = {},
  } = record ?? {};

  if (days && dailyWage) {
    dailyCost = days * dailyWage;
  }
  if (hours && hourlyWage) {
    hourlyCost = hours * hourlyWage;
  }

  // Sum both calculations if both are available
  return dailyCost + hourlyCost;
};

/**
 * Calculate estimated cost from hourly wage and hours
 * @param {number} hourlyWage - Hourly wage rate
 * @param {number} hours - Number of hours
 * @returns {number} Calculated estimated cost
 */
export const calculateEstimatedCostFromHours = (hourlyWage, hours) => hourlyWage * hours;

const ESTIMATE_BASE_STYLE = {
  fontSize: 14,
  color: Colors.GRAY_950,
  lineHeight: '18px',
  marginBottom: 0,
};

export const ESTIMATED_DISPLAY_TEXT_STYLE = {
  ...ESTIMATE_BASE_STYLE,
  fontFamily: 'open-sans-500',
};

export const ESTIMATED_LABEL_STYLE = {
  ...ESTIMATE_BASE_STYLE,
  fontFamily: 'open-sans-600',
};

/**
 * Builds table data for costcodes with parent/child structure for multi-category.
 * Single-category: one flat row (editable). Multi-category: one parent row (read-only, sums)
 * with expandable children (one per category, editable). Works for unphased or phased.
 * @param {Array} costcodes - Array of costcodes
 * @param {Object} options - Optional phase info
 * @param {string|null} options.phaseId - Phase id when phased
 * @param {string|null} options.phaseName - Phase name when phased
 * @returns {Array} - Rows: flat (single-category) or parent with children
 */
export const buildCostcodesTableData = (costcodes = [], options = {}) => {
  const { phaseId = null, phaseName = null } = options;
  const result = [];

  costcodes.forEach((costcode) => {
    const { categoryIds = [], categoryAssignments = {} } = costcode;
    if (categoryIds.length === 0) return;

    const keyPrefix = phaseId ? `${phaseId}-` : '';

    if (categoryIds.length === 1) {
      const categoryId = categoryIds[0];
      result.push({
        ...costcode,
        categoryId,
        activeCategoryId: categoryId,
        categoryAssignment: categoryAssignments[categoryId] || {},
        rowKey: `${keyPrefix}${costcode.id}-${categoryId}`,
        phaseId,
        phaseName,
      });
      return;
    }

    let totalEstimatedCost = 0;
    let totalHours = 0;
    let totalDays = 0;
    let totalEstimatedQuantity = 0;
    categoryIds.forEach((catId) => {
      const assign = categoryAssignments[catId] || {};
      totalEstimatedCost += Number(assign.estimatedCost) || 0;
      totalHours += Number(assign.hours) || 0;
      totalDays += Number(assign.days) || 0;
      totalEstimatedQuantity += Number(assign.estimatedQuantity) || 0;
    });

    const children = categoryIds.map((categoryId) => ({
      ...costcode,
      categoryId,
      activeCategoryId: categoryId,
      categoryAssignment: categoryAssignments[categoryId] || {},
      rowKey: `${keyPrefix}${costcode.id}-${categoryId}`,
      phaseId,
      phaseName,
      isChildRow: true,
    }));

    result.push({
      ...costcode,
      rowKey: `${keyPrefix}parent-${costcode.id}`,
      phaseId,
      phaseName,
      isParentRow: true,
      categoryId: null,
      categoryAssignment: {
        estimatedCost: totalEstimatedCost,
        hours: totalHours,
        days: totalDays,
        estimatedQuantity: totalEstimatedQuantity,
        units: undefined,
      },
      children,
    });
  });

  return result;
};

export const renderDefaultColumn = (value) => value || '-';

/**
 * Stable key for a costcode in the table (id + phase). Used to group rows and detect edit scope.
 * @param {Object} record - Row from buildCostcodesTableData (has id, phaseId)
 * @returns {string}
 */
export const getCostcodeKey = (record) => {
  const { id } = record;
  const phaseId = record.phaseId ?? 'unphased';
  return `${id}-${phaseId}`;
};

/**
 * Collect all rows (including children) that belong to one costcode in the tree.
 * @param {Array} dataSource - Tree from buildCostcodesTableData
 * @param {string} costcodeKey - From getCostcodeKey(record)
 * @returns {Array}
 */
export const getRowsForCostcode = (dataSource, costcodeKey) => {
  const rows = [];
  function walk(list) {
    (list || []).forEach((r) => {
      if (getCostcodeKey(r) === costcodeKey) rows.push(r);
      if (r.children?.length) walk(r.children);
    });
  }
  walk(dataSource);
  return rows;
};

/**
 * Merge in-progress edits (map of rowKey → row) into the tree for one costcode.
 * Preserves categoryAssignment per row. Used for edit-in-place before save.
 * @param {Array} dataSource - Tree from buildCostcodesTableData
 * @param {Object} editingCostcodeData - { [rowKey]: partialRow }
 * @param {string} editingCostcodeKey - From getCostcodeKey(record)
 * @returns {Array} New tree with edits merged
 */
export const mergeEditingIntoDataSource = (dataSource, editingCostcodeData, editingCostcodeKey) => (
  (dataSource || []).map((row) => {
    const key = getCostcodeKey(row);
    const edited = key === editingCostcodeKey && editingCostcodeData[row.rowKey];
    const next = edited
      ? {
        ...row,
        ...edited,
        categoryAssignment: edited.categoryAssignment ?? row.categoryAssignment,
      }
      : row;
    if (next.children?.length) {
      next.children = mergeEditingIntoDataSource(
        next.children,
        editingCostcodeData,
        editingCostcodeKey,
      );
    }
    return next;
  })
);

export const UNSUPPORTED_DETAIL_KEYS = new Set([
  'actualSubcontractSpend',
  'uncategorizedCosts',
  'actualBurdenCost',
]);

export const getSummaryAllocatedHours = ({
  unphasedCostcodes,
  phases,
}) => {
  let totalAllocatedHours = 0;

  // unphased costcodes
  unphasedCostcodes.forEach((cc) => {
    const assignments = cc.categoryAssignments || {};
    Object.values(assignments).forEach((assignment) => {
      totalAllocatedHours += assignment?.hours ?? 0;
    });
  });

  // phased costcodes
  phases?.forEach(({ costcodes: phasedCostcodes }) => {
    phasedCostcodes.forEach((cc) => {
      const assignments = cc.categoryAssignments || {};
      Object.values(assignments).forEach((assignment) => {
        totalAllocatedHours += assignment?.hours ?? 0;
      });
    });
  });
  return totalAllocatedHours;
};

// natively supported categories
const OVER_BUDGET_PAIRS = [
  ['actualMaterialSpend', 'estimatedMaterialCost'],
  ['actualOverheadSpend', 'estimatedOverheadCost'],
  ['actualLaborSpend', 'estimatedLaborCost'],
  ['actualEquipmentSpend', 'estimatedEquipmentCost'],
];

/**
 * Calculate the number of over budget items in the summary
 * @param {Object} summary - Summary object
 * @returns {number} Number of over budget items
 */
export const getSummaryOverBudgetItems = (summary) => {
  let overBudgetItems = 0;

  // eslint-disable-next-line no-param-reassign
  OVER_BUDGET_PAIRS.forEach(([actualKey, estKey]) => {
    const actual = summary[actualKey] || 0;
    const estimated = summary[estKey] || 0;
    if (actual > estimated) overBudgetItems += 1;
  });

  // custom category estimates
  Object.keys(summary).forEach((key) => {
    if (key.startsWith('estimated_')) {
      const categoryId = key.slice(11);
      const actual = summary[categoryId] || 0;
      const estimated = summary[key] || 0;
      if (actual > estimated) overBudgetItems += 1;
    }
  });

  return overBudgetItems;
};

/**
 * Calculate the total uncategorized costs
 * @param {Object} uncategorizedInvoices - Array of uncategorized invoices
 * @param {Object} uncategorizedMaterialUpdates - Array of uncategorized material updates
 * @param {Object} uncategorizedEquipmentUpdates - Array of uncategorized equipment updates
 * @returns {Object} Object containing the total uncategorized costs
 */
export const getSummaryUncategorizedCosts = ({
  uncategorizedInvoices,
  uncategorizedMaterialUpdates,
  uncategorizedEquipmentUpdates,
}) => {
  // HARBOUR-8265: Include both undistributed amounts AND distributions without categories
  const uncategorizedCosts = uncategorizedInvoices.reduce((costs, invoice) => {
    const undistributedAmount = invoice.amount - invoice.amountDistributed;
    const needsCategoryAmount = invoice.uncategorizedDistributionAmount || 0;
    return costs + undistributedAmount + needsCategoryAmount;
  }, 0);

  const uncategorizedMaterialCosts = uncategorizedMaterialUpdates.reduce((costs, update) => (
    costs + (update.total)
  ), 0);

  const uncategorizedEquipmentCosts = uncategorizedEquipmentUpdates.reduce((costs, update) => (
    costs + (update.total)
  ), 0);

  return {
    uncategorizedInvoiceCosts: uncategorizedCosts,
    uncategorizedMaterialCosts,
    uncategorizedEquipmentCosts,
  };
};

/**
 * Builds the base summary object
 * @param {Object} options - Options object
 * @param {number} options.estimatedLaborCost - Estimated labor cost
 * @param {number} options.estimatedMaterialCost - Estimated material cost
 * @param {number} options.estimatedOverheadCost - Estimated overhead cost
 * @param {number} options.estimatedEquipmentCost - Estimated equipment cost
 * @param {number} options.estimatedBurdenRate - Estimated burden rate
 * @param {Array} options.projectCustomCategoryEstimates - Project custom category estimates
 * @returns {Object} Base summary object
 */
export const getBaseSummary = ({
  estimatedLaborCost,
  estimatedMaterialCost,
  estimatedOverheadCost,
  estimatedEquipmentCost,
  estimatedBurdenRate,
  projectCustomCategoryEstimates,
}) => {
  const summary = {
    actualMaterialSpend: 0,
    actualOverheadSpend: 0,
    actualLaborSpend: 0,
    actualEquipmentSpend: 0,
    actualSubcontractSpend: 0,
    actualTotalSpend: 0,
    actualBurdenCost: 0,
    estimatedLaborCost: estimatedLaborCost || 0,
    estimatedMaterialCost: estimatedMaterialCost || 0,
    estimatedOverheadCost: estimatedOverheadCost || 0,
    estimatedEquipmentCost: estimatedEquipmentCost || 0,
    estimatedSubcontractCost: 0,
    estimatedTotalCost: (
      estimatedLaborCost
      + estimatedMaterialCost
      + estimatedOverheadCost
      + estimatedEquipmentCost
      + (estimatedBurdenRate * estimatedLaborCost)) || 0,
    estimatedBurdenCost: (estimatedBurdenRate * estimatedLaborCost) || 0,
  };

  // custom category estimates
  let customCategoryEstimatedTotal = 0;
  (projectCustomCategoryEstimates || []).forEach(({ categoryId, value }) => {
    if (categoryId == null) return;
    const estimated = value ?? 0;
    summary[`estimated_${categoryId}`] = estimated;
    summary[categoryId] = 0;
    customCategoryEstimatedTotal += Number(estimated) || 0;
  });
  summary.estimatedTotalCost += customCategoryEstimatedTotal;

  return summary;
};

/**
 * Attaches the actual spend and actual cost to a costcode row.
 * @param {Object} row - The costcode row to attach the costs to.
 * @param {Object} categoriesIdMap - The map of category ids to category names.
 * @param {Function} computeActualSpend - The function to compute the actual spend for a costcode.
 * @param {Function} computeActualCostForCostCode - Computes labor/wage cost for a costcode.
 * @returns {Object} The parsed costcode row.
 */
export const attachCostcodeCosts = (row, categoriesIdMap, computeActualSpend, getActualCost) => {
  const categoryName = categoriesIdMap[row.categoryId]?.name;

  if (row.isParentRow && row.children?.length) {
    const enrichedChildren = [];
    let actualSpend = 0;
    let actualCost = 0;
    row.children.forEach((child) => {
      const enrichedChild = attachCostcodeCosts(
        child,
        categoriesIdMap,
        computeActualSpend,
        getActualCost,
      );
      actualSpend += enrichedChild.actualSpend;
      actualCost += enrichedChild.actualCost;
      enrichedChildren.push(enrichedChild);
    });
    return {
      ...row,
      categoryName,
      actualSpend,
      actualCost,
      totalCost: actualSpend + actualCost,
      children: enrichedChildren,
    };
  }

  const actualSpend = computeActualSpend({
    phaseId: row.phaseId,
    costcodeId: row.id,
    defaultCategoryId: typeof row.categoryId === 'string' ? null : row.categoryId,
    customCategoryId: typeof row.categoryId === 'string' ? row.categoryId : null,
  });

  const hasActualCost = (
    categoryName === 'Labor'
    || categoryName === 'Overhead'
    || categoriesIdMap[row.categoryId]?.allowTimeTracking
  );

  const actualCost = hasActualCost
    ? getActualCost({ phaseId: row.phaseId, costcodeId: row.id })
    : 0;

  return {
    ...row,
    categoryName,
    actualSpend,
    actualCost,
    totalCost: actualSpend + actualCost,
  };
};

/**
 * Builds the phased data for the summary
 * @param {Object} options - Options object
 * @param {Array} options.phases - Array of phases
 * @param {string} options.projectId - Project id
 * @param {Object} options.costcodeIdMap - Map of costcode ids to costcodes
 * @param {Object} options.categorizedCostUpdateMap - Map of categorized cost update ids to cost update objects
 * @param {Object} options.categoryAssignments - Map of category assignments to costcode ids
 * @param {Object} options.categoriesIdMap - Map of category ids to category names
 * @param {Function} options.computeActualSpend - Function to compute the actual spend for a costcode
 * @param {Function} options.computeActualCostForCostCode - Function to compute the actual cost for a costcode
 * @returns {Array} Array of phased data
 */
export const getSummaryPhasedData = ({
  phases,
  projectId,
  costcodeIdMap,
  categorizedCostUpdateMap,
  categoryAssignments,
  categoriesIdMap,
  computeActualSpend,
  computeActualCostForCostCode,
}) => {
  const phaseIdMap = {};

  phases.forEach((phase) => {
    const {
      id,
      projectId: phaseProjectId,
      costcodeId,
      categoryAssignments: phaseCategoryAssignments,
      customCategoryEstimates: phaseCustomCategoryEstimates,
    } = phase;

    if (phaseProjectId !== projectId) return;

    if (!phaseIdMap[id]) {
      phaseIdMap[id] = {
        phaseId: id,
        costcodes: [],
        id,
        name: phase.name,
        description: phase.description,
        estimatedLabourHours: phase.estimatedLabourHours,
        estimatedLabourCost: phase.estimatedLabourCost,
        estimatedOverheadHours: phase.estimatedOverheadHours,
        estimatedOverheadCost: phase.estimatedOverheadCost,
        estimatedMaterialCost: phase.estimatedMaterialCost,
        estimatedEquipmentCost: phase.estimatedEquipmentCost,
        estimatedEquipmentHours: phase.estimatedEquipmentHours,
        type: 'phase',
      };

      phaseCustomCategoryEstimates?.forEach(({ categoryId, value }) => {
        if (!categoryId || !value) return;
        phaseIdMap[id][`phaseEstimate.${categoryId}`] = value;
      });
    }

    const { [costcodeId]: fullCostcode = {} } = costcodeIdMap;

    if (!fullCostcode.id || fullCostcode.projectId !== projectId) return;

    const assignments = categoryAssignments?.[costcodeId]?.[id] ?? {};
    const baseCostcode = {
      ...fullCostcode,
      projectId,
      phaseId: id,
      costcodeId: fullCostcode.id,
      categoryAssignments: phaseCategoryAssignments ?? assignments,
    };
    phaseIdMap[id].costcodes.push(baseCostcode);
  });

  // Per-phase: costcodes table data (enriched) for that phase
  const phasesData = Object.values(phaseIdMap).map((phase) => {
    const {
      phaseId,
      name: phaseName,
      costcodes: phaseCostcodes,
    } = phase;

    const phaseTableData = buildCostcodesTableData(phaseCostcodes, {
      phaseId,
      phaseName,
    });

    const phaseCostcodesTableData = enrichRowsWithCostUpdates(
      phaseTableData.map((row) => (
        attachCostcodeCosts(
          row,
          categoriesIdMap,
          computeActualSpend,
          computeActualCostForCostCode,
        )
      )),
      categorizedCostUpdateMap,
    );

    return {
      ...phase,
      phaseId,
      phaseName,
      costcodes: phaseCostcodesTableData,
    };
  });

  return phasesData;
};
